import React, {
  ChangeEvent,
  ClipboardEvent,
  CSSProperties,
  KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import { StyledTableCell } from "../ShipmentSummary/components/SummaryGrid/SummaryGrid";
import Input from "@mui/material/Input";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { InputBaseProps } from "@mui/material";
import { BACKGROUND_GRAY, BLUE_40 } from "../../constants/css.constants";
import Box from "@mui/material/Box";

type ClickableCellProps = {
  disabled: boolean;
  value?: number;
  updateData?: (value: string) => void;
  transform: (value: number) => string;
  styles: CSSProperties;
  onPaste?: (e: ClipboardEvent<HTMLTableCellElement>) => void;
  onClick?: () => void;
  type: InputBaseProps["type"];
  isSelected?: boolean;
  setNextCell?: () => void;
};

const lightTheme = createTheme({ palette: { mode: "light" } });
const ClickableCell = ({
  disabled,
  value,
  onPaste,
  transform,
  styles,
  updateData,
  onClick,
  type,
  isSelected,
  setNextCell,
}: ClickableCellProps) => {
  const [isDisabled, setIsDisabled] = useState(true);
  const [val, setVal] = useState<string>(value ? `${value}` : "0");
  const [isHighlighted, setIsHighlighted] = useState(false);
  const wrapperRef = useRef<Element | null>(null);

  useEffect(() => {
    if (value !== parseInt(val)) {
      value = value ? Math.round(value) : value;
      setVal(`${value}`);
    }
  }, [value]);

  useEffect(() => {
    if (isSelected) {
      setIsHighlighted(true);
      setIsDisabled(false);
    }
  }, [isSelected]);
  const handleClickOutside = (event) => {
    if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
      setIsHighlighted(false);
      setIsDisabled(true);
    }
  };
  useEffect(() => {
    document.addEventListener("click", handleClickOutside, false);
    return () => {
      document.removeEventListener("click", handleClickOutside, false);
    };
  }, []);

  const handleSingleClick = () => {
    if (disabled) return;
    setIsDisabled(!isDisabled);
    setIsHighlighted(!isHighlighted);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setVal(value);
    if (updateData) updateData(value.length ? value : "0");
  };

  const handleKeys = () => {
    if (!val.length) {
      setVal("0");
      if (updateData) updateData("0");
    }
    if (setNextCell) {
      setNextCell();
    }
    setIsDisabled(true);
    setIsHighlighted(false);
  };
  const handleKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!disabled) {
      if (e.key === "Enter") {
        handleKeys();
      } else if (e.key === "Escape") {
        setIsDisabled(true);
        setIsHighlighted(false);
      }
    }
  };

  const handleKeydown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!disabled) {
      if (e.key === "Tab") {
        e.preventDefault();
        handleKeys();
      }
    }
  };

  return (
    <StyledTableCell
      ref={wrapperRef}
      onClick={handleSingleClick}
      onPaste={onPaste}
      style={{
        height: "10px",
        maxHeight: "10px",
        fontSize: "1rem",
        boxShadow: isHighlighted ? `inset 0 0 0 2px ${BLUE_40}` : "none",
        cursor: disabled ? "default" : "pointer",
        backgroundColor: disabled ? BACKGROUND_GRAY : "inherit",
        color: disabled ? "#FFF" : "inherit",
        ...styles,
      }}
      padding='none'
      size='small'
    >
      {isDisabled ? (
        transform(value ?? 0)
      ) : (
        //@ts-ignore
        <ThemeProvider theme={lightTheme}>
          <Input
            style={{ maxWidth: "300px", maxHeight: "22px", marginLeft: "5px", overflow: "hidden" }}
            autoFocus
            onFocus={(e) => e.target.select()}
            onPaste={(e) => e.preventDefault()}
            type={type}
            onKeyUp={handleKeyPress}
            onKeyDown={handleKeydown}
            onChange={handleChange}
            value={val}
            disableUnderline={true}
          ></Input>
        </ThemeProvider>
      )}
    </StyledTableCell>
  );
};

export default ClickableCell;
